// User Role — the user-role design: the roles on the left, the role open on the
// right with its four permission screens as tabs instead of four popups.
//
// The list, the search, the level rules, the permissions and the delete call
// are unchanged; what moved is where they are drawn.
import React, { useEffect, useMemo, useState } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { setheadertoggle } from "../../redux/toggle";
import { fetchUserRole } from "../../redux/userSetting";
import RoleDetail from "../../components/adminsitration/userroles/RoleDetail";
import { deleteUserRole } from "../../services/AdminApiServices";
import {
  AdminScreen,
  AdminList,
  AxSegments,
  AxStatusKeys,
  AxEmptyDetail,
  AxPlusIcon,
  AxRefreshIcon,
  AxPrintIcon,
} from "../../components/adminsitration/ui";

const SORTS = [
  { key: "name", label: "Role" },
  { key: "level", label: "Level" },
];

const UserRole = () => {
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [updateData, setUpdateData] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [seg, setSeg] = useState("all");
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [levels, setlevels] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);

  const dispatch = useDispatch();
  const { userRoles } = useSelector((state) => state.usersetting);
  const { menuItems } = useSelector((state) => state.amc);
  const { togglehead } = useSelector((state) => state.headerToggle);
  const [permit, setpermit] = useState();

  useEffect(() => {
    setpermit(menuItems?.menuPermissions?.find((e) => e.menu.name == "User Role"));
  }, []);
  useEffect(() => {
    handleLevel();
  }, [userRoles]);

  const handletogglehead = () => {
    dispatch(setheadertoggle(!togglehead));
  };

  // the levels nobody is using yet — the form offers these, plus the role's own
  const handleLevel = () => {
    const newlevel = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].filter((level) =>
      userRoles?.every((role) => role.level !== level)
    );
    setlevels(newlevel);
  };

  const handleSearch = (e) => setSearchText(e.target.value);

  const searched = useMemo(
    () =>
      (userRoles || []).filter((value) =>
        value.name?.toLowerCase().includes(searchText?.toLowerCase())
      ),
    [userRoles, searchText]
  );

  const list = useMemo(() => {
    let out = searched;
    if (seg === "active") out = out.filter((r) => r.active);
    if (seg === "inactive") out = out.filter((r) => !r.active);
    const { key, dir } = sort;
    return out.slice().sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return (
        String(av ?? "").localeCompare(String(bv ?? ""), undefined, { sensitivity: "base" }) * dir
      );
    });
  }, [searched, seg, sort]);

  const counts = useMemo(
    () => ({
      all: searched.length,
      active: searched.filter((r) => r.active).length,
      inactive: searched.filter((r) => !r.active).length,
    }),
    [searched]
  );

  const handleEditClick = (record) => {
    setModalMode("edit");
    handleLevel();
    const data = userRoles?.find((e) => record.guid == e.guid);
    setUpdateData(data);
    setSelectedKey(record.guid);
    setModalOpen(true);
  };

  const MySwal = withReactContent(Swal);

  const handleDeleteClick = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteUserRole(id);
          if (response?.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The User Role has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            });
            setModalOpen(false);
            setSelectedKey(null);
            dispatch(fetchUserRole()); // Refresh the list
            handleLevel();
          } else {
            MySwal.fire({
              title: "Error!",
              text: response.Message,
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error(error?.response?.data?.Message);
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.Message,
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const renderRow = (r) => ({
    name: r.name,
    tags: r.active ? null : <span className="axa-tag off">Inactive</span>,
    inactive: !r.active,
    sub: (
      <>
        <span className="code">Level {r.level}</span>
        <span>{r.active ? "Signs in" : "Cannot sign in"}</span>
      </>
    ),
    right: (
      <>
        <span className="axa-lvl">L{r.level}</span>
        <span className={"axa-dot " + (r.active ? "on" : "off")} />
      </>
    ),
  });

  return (
    <AdminScreen
      title="User Role"
      subtitle="Roles and what each one may reach — on one screen"
      searchValue={searchText}
      onSearchChange={handleSearch}
      onSearchClear={() => setSearchText("")}
      searchPlaceholder="Search by name…"
      searchHint={null}
      tools={
        <>
          <button
            type="button"
            className="axa-iconbtn"
            title="Reload"
            onClick={() => {
              dispatch(fetchUserRole());
              setSearchText("");
              setSeg("all");
              handleLevel();
            }}
          >
            <AxRefreshIcon />
          </button>
          <button
            type="button"
            className="axa-iconbtn"
            title="Print this role's permissions"
            onClick={() => window.print()}
          >
            <AxPrintIcon />
          </button>
          <button
            type="button"
            className="axa-iconbtn"
            aria-pressed={!!togglehead}
            title="Collapse the app header"
            onClick={handletogglehead}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M4 10l4-4 4 4" />
            </svg>
          </button>
        </>
      }
      actions={
        permit?.add ? (
          <button
            type="button"
            className="axa-btn axa-btn-primary"
            onClick={() => {
              setModalMode("add");
              setUpdateData({});
              setSelectedKey(null);
              handleLevel();
              setModalOpen(true);
            }}
          >
            <AxPlusIcon />
            New role
          </button>
        ) : null
      }
      segments={
        <AxSegments
          value={seg}
          onChange={setSeg}
          items={[
            { key: "all", label: "All", count: counts.all },
            { key: "active", label: "Active", count: counts.active },
            { key: "inactive", label: "Inactive", count: counts.inactive },
          ]}
        />
      }
      list={
        <AdminList
          items={list}
          keyOf={(r) => r.guid}
          selectedKey={selectedKey}
          onSelect={handleEditClick}
          renderRow={renderRow}
          sorts={SORTS}
          sort={sort}
          onSort={(key) =>
            setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }))
          }
          emptyTitle="No role matches"
          emptyHint="Try a shorter search, or another filter."
        />
      }
      detail={
        modalOpen ? (
          <RoleDetail
            key={modalMode + (updateData?.guid || "new")}
            mode={modalMode}
            data={modalMode === "edit" ? updateData : {}}
            levels={levels}
            canEdit={modalMode === "edit" ? !!permit?.edit : !!permit?.add}
            canDelete={!!permit?.deletion}
            onDelete={(row) => handleDeleteClick(row.id)}
            handleClose={() => {
              setModalOpen(false);
              setSelectedKey(null);
            }}
          />
        ) : (
          <AxEmptyDetail title="Pick a role">
            Choose a role on the left to see and change what it can reach. Web and Windows
            permissions are tabs here — no separate windows to open and lose.
          </AxEmptyDetail>
        )
      }
      status={
        <AxStatusKeys
          keys={[
            { k: "Click", t: "Open a role" },
            { k: "Tabs", t: "Web / Windows permissions" },
          ]}
          right={`${list.length} of ${userRoles?.length || 0} roles`}
        />
      }
    />
  );
};

export default UserRole;
